<template>
  <div class="container-fluid">
    <div class="row starter-main mt-5 pt-5">
      <TableTitle
        :title="title"
        :pageTitle="pageTitle"
        icon="fas fa-question-circle"
        :showAction="true"
        actionLabel="Create New"
        :modal="'createNew'"
        :disabled="!canCreate('website')"
      />
      <CommonTable2
        :tableObj="tableConfig"
        @edit="handleEdit"
        @delete="handleDelete"
        @view="handleView"
        @toggle-status="toggleStatus"
        permissionKey="website"
      />
    </div>
  </div>

  <!-- pagination -->
  <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
    <!-- Rows per page -->
    <div class="d-flex align-items-center gap-2">
      <span class="text-muted">Rows per page:</span>
      <select
        class="form-select form-select-sm rows-select"
        v-model="tablePagination.per_page"
        @change="changeRows"
      >
        <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
          {{ n }}
        </option>
      </select>
    </div>
    <!-- Pagination Buttons -->
    <div class="pagination-mini">
      <!-- Previous -->
      <button
        class="page-btn prev_btn"
        :disabled="!tablePagination.has_prev"
        @click="handlePageChange(tablePagination.current_page - 1)"
      >
        «
      </button>
      <!-- Numbered Pages -->
      <button
        v-for="(p, index) in visiblePages"
        :key="index"
        class="page-btn"
        :class="{
          'page-active': p === tablePagination.current_page,
          'disabled-dots': p === '...',
        }"
        :disabled="p === '...'"
        @click="p !== '...' && typeof p === 'number' && handlePageChange(p) "
      >
        {{ p }}
      </button>

      <!-- Next -->
      <button
        class="page-btn next_btn"
        :disabled="!tablePagination.has_next"
        @click="handlePageChange(tablePagination.current_page + 1)"
      >
        »
      </button>
    </div>
  </div>

  <!-- Create Modal -->
  <BaseModal
    id="createNew"
    :title="`Create ${pageTitle}`"
    :visible="isCreateModalOpen"
    @close="closeCreateModal"
    @save="handleSaveCreate"
    saveButtonText="Create"
  >
    <template #body>
      <BaseForm
        :fields="createFormFields"
        v-model="newItemData"
        @submit="handleSaveCreate"
        ref="createFormRef"
      />
    </template>
  </BaseModal>

  <!-- Edit/View Modal -->
  <BaseModal
    id="editModal"
    :title="`${isViewMode ? 'View' : 'Edit'} ${pageTitle}`"
    :visible="isEditModalOpen"
    @close="closeEditModal"
    @save="handleSaveEdit"
    :show-save-button="!isViewMode"
  >
    <template #body>
      <BaseForm
        :fields="editFormFields"
        v-model="editingItemData"
        @submit="handleSaveEdit"
        ref="editFormRef"
      />
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const API_RESOURCE = "/common/faqs";
const pageTitle = "FAQ";
const title = "FAQs List";

// Use the generic CRUD composable
const crud = useCrud(API_RESOURCE);

// pagination local UI state
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const currentPage = ref(1);

// visiblePages computed
const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  if (!total || total <= 4) {
    return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  pages.push(1);

  if (current > 4) pages.push("...");

  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);

  if (current < total - 3) pages.push("...");

  pages.push(total);
  return pages;
});

// Table configuration
const rows = ref<any[]>([]);
const tableConfig = ref({
  columns: [
    { name: "Question", field: "question", sortable: true, width: "300px" },
    { name: "Answer", field: "answer", sortable: true, width: "400px" },
    { name: "Category", field: "category", sortable: true, width: "150px" },
    { name: "Order", field: "order", sortable: true, width: "80px" },
    { name: "Active", field: "is_active", type:'toggle_status', sortable: false, width: "80px" },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  showStatusFilter: false,
  table_width: "max-content",
});

watch(crud.data, (newVal) => {
  console.log(crud.data, 'crud, data')  
  rows.value = newVal || [];
});
watch(crud.isLoading, (newVal) => {
  tableConfig.value.loading = newVal;
});

// Sync backend pagination into tablePagination
watch(
  () => crud.pagination.value,
  (newVal) => {
    if (!newVal) return;

    tablePagination.value.current_page = newVal.current_page || 1;
    tablePagination.value.total_pages = newVal.total_pages || 1;
    tablePagination.value.total = newVal.total || 0;
    tablePagination.value.per_page = tablePagination.value.per_page;
    tablePagination.value.has_prev = tablePagination.value.current_page > 1;
    tablePagination.value.has_next =
    tablePagination.value.current_page < tablePagination.value.total_pages;
  },
  { immediate: true, deep: true }
);

// === ACTIONS ===
const changeRows = async () => {
  currentPage.value = 1;
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  if (
    tablePagination.value.total_pages &&
    page > tablePagination.value.total_pages
  )
    return;

  currentPage.value = page;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

// --- Modal State ---
const isCreateModalOpen = ref(false);
const isEditModalOpen = ref(false);
const isViewMode = ref(false);

// --- Form Data ---
const newItemData = ref({
  question: "",
  answer: "",
  category: "",
  order: 0,
  is_active: true,
});

const editingItemData = ref<{
  _id: string;
  question: string;
  answer: string;
  category: string;
  order: number;
  is_active: boolean;
}>({
  _id: "",
  question: "",
  answer: "",
  category: "",
  order: 0,
  is_active: true,
});

// --- Form Definitions ---
const createFormFields: FormField[] = [
  {
    key: "question",
    label: "Question",
    type: "text",
    required: true,
    placeholder: "Enter question",
  },
  {
    key: "answer",
    label: "Answer",
    type: "textarea",
    required: true,
    placeholder: "Enter answer",
  },
  {
    key: "category",
    label: "Category",
    type: "text",
    required: false,
    placeholder: "Enter category",
  },
  {
    key: "order",
    label: "Order",
    type: "number",
    required: false,
    placeholder: "Enter display order",
  },
  {
    key: "is_active",
    label: "Active",
    type: "checkbox",
    required: false,
  },
];

const editFormFields = computed<FormField[]>(() => [
  {
    key: "question",
    label: "Question",
    type: "text",
    required: true,
    placeholder: "Enter question",
    readonly: isViewMode.value,
  },
  {
    key: "answer",
    label: "Answer",
    type: "textarea",
    required: true,
    placeholder: "Enter answer",
    readonly: isViewMode.value,
  },
  {
    key: "category",
    label: "Category",
    type: "text",
    required: false,
    placeholder: "Enter category",
    readonly: isViewMode.value,
  },
  {
    key: "order",
    label: "Order",
    type: "number",
    required: false,
    placeholder: "Enter display order",
    readonly: isViewMode.value,
  },
  {
    key: "is_active",
    label: "Active",
    type: "checkbox",
    required: false,
    readonly: isViewMode.value,
  },
]);

// --- Form Validation (done in handler to avoid reactivity timing issues) ---

// --- Handlers ---
const handleEdit = (row: any) => {
  isViewMode.value = false;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  editingItemData.value = { ...row };
  isEditModalOpen.value = true;
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this FAQ?")) {
    await crud.deleteItem(id);
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};

const handleSaveCreate = async () => {
  if (!newItemData.value.question?.toString().trim()) {
    alert("Question is required.");
    return;
  }
  if (!newItemData.value.answer?.toString().trim()) {
    alert("Answer is required.");
    return;
  }

  const result = await crud.createItem(newItemData.value);
  if (result) {
    closeCreateModal();
    await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
  }
};

const handleSaveEdit = async () => {
  const id = editingItemData.value._id;
  if (!id) return;
  const { _id, ...updatePayload } = editingItemData.value;
  const result = await crud.updateItem(id, updatePayload);
  if (result) {
    closeEditModal();
    await crud.fetchData({
      page: tablePagination.value.current_page,
      limit: tablePagination.value.per_page,
    });
  }
};
const toggleStatus = async (id: string, status: boolean) => {
  if (!id) return;

  try {
    const result = await crud.updateItem(id, {
      is_active: status
    });

    if (result) {
      // update local row instantly (optional but recommended)
      const row = rows.value.find(r => r._id === id);
      if (row) {
        row.is_active = status;
      }

      // or refetch from server
      await crud.fetchData({
        page: tablePagination.value.current_page,
        limit: tablePagination.value.per_page,
      });
    }

  } catch (err) {
    console.error("Failed to update status:", err);
  }
};


const closeCreateModal = () => {
  isCreateModalOpen.value = false;
  newItemData.value = {
    question: "",
    answer: "",
    category: "",
    order: 0,
    is_active: true,
  };
};

const closeEditModal = () => {
  isEditModalOpen.value = false;
  isViewMode.value = false;
  editingItemData.value = {
    _id: "",
    question: "",
    answer: "",
    category: "",
    order: 0,
    is_active: true,
  };
};

// --- lifecycle: initial load ---
onMounted(async () => {
  await crud.fetchData({
    page: tablePagination.value.current_page,
    limit: tablePagination.value.per_page,
  });
});
</script>

<style scoped>
/* pagination style */
.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Active page */
.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.page-active:hover {
  background: #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
</style>